'use client';
/** Drag-to-reorder, the app's one mechanism for it (`@dnd-kit` underneath). Two arrangements share this file's
 *  item seam: a flat list or grid (`SortableList`, the Project register) and containers with items
 *  (`SortableBoard.tsx`, a board's columns and cards).
 *
 *  An item hands its caller a render state, never a wrapper element: where the moving box is, which element is
 *  the handle, and the transform to paint. Movement is a transform on the painted box only, so the cells of a
 *  grid never change size and nothing below a travelling item jumps. dnd-kit owns sensors, collision,
 *  auto-scroll near the edges and the live region; the caller owns the words, given as templates. */
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type Announcements, type DragEndEvent, type DraggableSyntheticListeners, type UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRef, type CSSProperties, type KeyboardEventHandler, type MouseEventHandler, type PointerEventHandler, type ReactNode, type TouchEventHandler } from 'react';

export type SortableId = string | number;

/** How long a press has to rest before it picks an item up, for a finger and (in a flat list) for a mouse. */
export const SORTABLE_HOLD_MS = 400;
/** How far a press may drift while it is held; past this it is a scroll. On a board the same distance is
 *  what a mouse has to travel before a press on a card stops being a click. */
export const SORTABLE_DRIFT_PX = 8;
export const HOLD_ACTIVATION = { activationConstraint: { delay: SORTABLE_HOLD_MS, tolerance: SORTABLE_DRIFT_PX } };

/** Live-region templates: `{name}`, `{position}` and `{total}` (1-based); a board's items also `{container}`. */
export interface SortableAnnouncements {
  pickedUp: string;
  /** Spoken only when the position the item would land on changes. */
  moved: string;
  dropped: string;
  cancelled: string;
}

export interface SortableListLabels extends SortableAnnouncements {
  instructions: string;
}

/** What a handle spreads before its own props: dnd-kit's accessibility attributes and the sensors that start
 *  from it. Its own press never reaches an enclosing `ContextMenuTrigger`, and the menu event a held finger
 *  raises on it is the move's, so neither opens a menu over a drag. */
export interface SortableHandleProps {
  role?: string;
  tabIndex?: number;
  'aria-disabled'?: boolean;
  'aria-pressed'?: boolean;
  'aria-roledescription'?: string;
  'aria-describedby'?: string;
  onMouseDown?: MouseEventHandler<HTMLElement>;
  onTouchStart?: TouchEventHandler<HTMLElement>;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  onPointerDown: PointerEventHandler<HTMLElement>;
  onContextMenu: MouseEventHandler<HTMLElement>;
}

/** The render state of one sortable thing. */
export interface SortableItemState {
  /** The painted box that travels. */
  setNodeRef: (node: HTMLElement | null) => void;
  /** The handle; keyboard focus returns to it after a drop. */
  setHandleRef: (node: HTMLElement | null) => void;
  handleProps: SortableHandleProps;
  /** Transform and transition only. */
  style: CSSProperties;
  /** This is the item being carried. */
  dragging: boolean;
}

type Sortable = ReturnType<typeof useSortable>;
type SensorEvent = 'onMouseDown' | 'onTouchStart' | 'onKeyDown';

/** One dnd-kit sensor listener, typed for the element it is spread on. */
export function listenerOf<Handler>(listeners: DraggableSyntheticListeners, event: SensorEvent): Handler | undefined {
  return listeners?.[event] as Handler | undefined;
}

/** The item state with the handle carrying the given sensors. */
export function itemState(sortable: Sortable, handleEvents: readonly SensorEvent[]): SortableItemState {
  const handleProps: SortableHandleProps = {
    ...sortable.attributes,
    onPointerDown: (event) => event.stopPropagation(),
    onContextMenu: (event) => { event.preventDefault(); event.stopPropagation(); },
  };
  for (const event of handleEvents) {
    const listener = listenerOf<never>(sortable.listeners, event);
    if (listener) handleProps[event] = listener;
  }
  return {
    setNodeRef: sortable.setNodeRef,
    setHandleRef: sortable.setActivatorNodeRef,
    handleProps,
    style: { transform: CSS.Translate.toString(sortable.transform), transition: sortable.transition },
    dragging: sortable.isDragging,
  };
}

/** A template with its placeholders filled. */
export function say(template: string, values: Record<'name' | 'position' | 'total', string | number> & { container?: string }): string {
  let text = template;
  for (const [key, value] of Object.entries(values)) text = text.replaceAll(`{${key}}`, String(value));
  return text;
}

export interface SortableListProps<Id extends SortableId> {
  /** The items on screen, in order. */
  ids: readonly Id[];
  /** The whole order a position is counted in, when a search leaves only some items on screen. */
  order?: readonly Id[];
  nameOf: (id: Id) => string;
  labels: SortableListLabels;
  /** Dropped on another item: the carried item takes that item's place in the order. */
  onMove: (activeId: Id, overId: Id) => void;
  children: ReactNode;
}

/** A flat sortable list or grid. Every pointer drag starts from a held press on an item's handle, so a touch
 *  that starts scrolling before the hold is over never becomes a drag, and the keyboard starts from the same
 *  handle. */
export function SortableList<Id extends SortableId>({ ids, order = ids, nameOf, labels, onMove, children }: SortableListProps<Id>) {
  const sensors = useSensors(
    useSensor(MouseSensor, HOLD_ACTIVATION),
    useSensor(TouchSensor, HOLD_ACTIVATION),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const position = (id: UniqueIdentifier) => order.indexOf(id as Id);
  const speak = (template: string, active: UniqueIdentifier, at: UniqueIdentifier) => say(template, {
    name: nameOf(active as Id), position: position(at) + 1, total: order.length,
  });
  // dnd-kit reports `over` once at pick-up, on the item itself. Announcing it would replace "picked up" with a
  // move that did not happen, so a position is spoken only when it differs from the last one spoken.
  const spokenPosition = useRef(0);
  const announcements: Announcements = {
    onDragStart: ({ active }) => { spokenPosition.current = position(active.id); return speak(labels.pickedUp, active.id, active.id); },
    onDragOver: ({ active, over }) => {
      const at = over?.id ?? active.id;
      if (position(at) === spokenPosition.current) return undefined;
      spokenPosition.current = position(at);
      return speak(labels.moved, active.id, at);
    },
    onDragEnd: ({ active, over }) => speak(labels.dropped, active.id, over?.id ?? active.id),
    onDragCancel: ({ active }) => speak(labels.cancelled, active.id, active.id),
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onMove(active.id as Id, over.id as Id);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      accessibility={{ announcements, screenReaderInstructions: { draggable: labels.instructions } }}
    >
      <SortableContext items={[...ids]} strategy={rectSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
}

/** One item of a `SortableList`: mouse, finger and keyboard all start from its handle. */
export function useSortableItem(id: SortableId, options: { disabled?: boolean } = {}): SortableItemState {
  return itemState(useSortable({ id, disabled: options.disabled }), ['onMouseDown', 'onTouchStart', 'onKeyDown']);
}
